// src/components/SupportWidget.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ensureUserId,
  listTickets,
  createTicket,
  listMessages,
  postMessage,
  markTicketReadForUser,
  type SupportTicket,
  type SupportMessage,
} from "../api/support";

type Props = {
  // необязательно, но можно передавать, если открываем поддержку со страницы оплаты/курса
  defaultSubject?: string;
  defaultCategory?: string | null;
  courseId?: number | string | null;
  source?: "dashboard" | "payment" | "other";
};

function getClientId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("clientId");
}

const categories = [
  { value: "oplata", label: "Оплата" },
  { value: "dostup", label: "Доступ" },
  { value: "refund", label: "Возврат" },
  // добавишь свои при необходимости
];

const statusBadge: Record<string, string> = {
  open: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  resolved: "bg-blue-100 text-blue-800",
  closed: "bg-gray-200 text-gray-700",
};

// === ДОБАВЛЕНО: локальные лимиты фронта ===
const CLIENT_COOLDOWN_SEC = 15;
const MAX_USER_STREAK = 5;

const SupportWidget: React.FC<Props> = ({
  defaultSubject = "",
  defaultCategory = null,
  courseId = null,
  source = "dashboard",
}) => {
  const [userId, setUserId] = useState<number | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // создание нового тикета
  const [newSubject, setNewSubject] = useState<string>(defaultSubject);
  const [newCategory, setNewCategory] = useState<string | "">(
    defaultCategory ?? ""
  );
  const [newBody, setNewBody] = useState<string>("");

  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const selectedTicket = useMemo(
    () => tickets.find((t) => t.id === selectedId) || null,
    [tickets, selectedId]
  );

  // === ДОБАВЛЕНО: состояние и таймер кулдауна ===
  const [cooldownLeft, setCooldownLeft] = useState<number>(0);
  const cooldownActive = cooldownLeft > 0;
  useEffect(() => {
    if (!cooldownActive) return;
    const id = setInterval(
      () => setCooldownLeft((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [cooldownActive]);

  // === ДОБАВЛЕНО: стрик пользовательских сообщений подряд ===
  const userStreak = useMemo(() => {
    let s = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.author_type === "user") s++;
      else break;
    }
    return s;
  }, [messages]);
  const streakBlocked = userStreak >= MAX_USER_STREAK;

  // автоскролл вниз при появлении сообщений
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // инициализация пользователя
  useEffect(() => {
    (async () => {
      const cid = getClientId();
      if (!cid) {
        setError(
          "Нет идентификатора пользователя (clientId). Пожалуйста, войдите заново."
        );
        return;
      }
      try {
        const uid = await ensureUserId(cid);
        setUserId(uid);
      } catch (e) {
        console.error(e);
        setError("Не удалось инициализировать пользователя.");
      }
    })();
  }, []);

  // загрузка тикетов
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        setTicketsLoading(true);
        const list = await listTickets(userId);
        setTickets(list);
        // если ничего не выбрано — выбрать самый свежий
        if (list.length && !selectedId) {
          setSelectedId(list[0].id);
        }
      } catch (e) {
        console.error(e);
        setError("Не удалось загрузить тикеты.");
      } finally {
        setTicketsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // загрузка сообщений выбранного тикета
  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      try {
        setMessagesLoading(true);
        const msgs = await listMessages(selectedId);
        setMessages(msgs);
        // пометим прочитанным для пользователя
        await markTicketReadForUser(selectedId);
        // обновим флаг в локальном списке
        setTickets((prev) =>
          prev.map((t) =>
            t.id === selectedId ? { ...t, unread_for_user: false } : t
          )
        );
      } catch (e) {
        console.error(e);
        setError("Не удалось загрузить сообщения.");
      } finally {
        setMessagesLoading(false);
      }
    })();
  }, [selectedId]);

  async function handleCreateTicket(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!userId) return;
    if (!newSubject.trim() || !newBody.trim()) {
      setError("Укажите тему и сообщение.");
      return;
    }
    try {
      setCreating(true);
      // ВАЖНО: первое сообщение уходит прямо в createTicket, отдельный postMessage не нужен
      const ticket = await createTicket({
        userId, // для совместимости с типами, на сервере игнорируется
        subject: newSubject.trim(),
        category: newCategory || null,
        source,
        course_id: courseId ? Number(courseId) : null,
        body: newBody.trim(), // <-- передаем текст первого сообщения во Flow Create
      });

      // обновим список и выберем тикет
      setTickets((prev) => [ticket, ...prev]);
      setSelectedId(ticket.id);
      setNewSubject("");
      setNewCategory("");
      setNewBody("");
    } catch (e) {
      console.error(e);
      setError("Не удалось создать тикет.");
    } finally {
      setCreating(false);
    }
  }

  async function handleSendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTicket || !userId) return;
    const text = newBody.trim();
    if (!text) return;

    // === ДОБАВЛЕНО: локальная проверка кулдауна 15 сек по последнему user-сообщению ===
    if (!cooldownActive) {
      const lastUserMsg = [...messages].reverse().find((m) => m.author_type === "user");
      if (lastUserMsg?.date_created) {
        const diffSec = Math.floor(
          (Date.now() - new Date(lastUserMsg.date_created).getTime()) / 1000
        );
        const left = CLIENT_COOLDOWN_SEC - diffSec;
        if (left > 0) {
          setCooldownLeft(left);
          return;
        }
      }
    }

    // === ДОБАВЛЕНО: запрет >5 подряд без ответа агента ===
    if (streakBlocked) {
      // мягко блокируем без общей красной ошибки страницы
      return;
    }

    try {
      setSending(true);
      const msg = await postMessage({
        ticketId: selectedTicket.id,
        userId,
        body: text,
      });
      setMessages((prev) => [...prev, msg]);
      setNewBody("");
      // обновим «последнее» время в карточке тикета
      setTickets((prev) =>
        prev.map((t) =>
          t.id === selectedTicket.id
            ? { ...t, last_message_at: msg.date_created || new Date().toISOString() }
            : t
        )
      );
      // === ДОБАВЛЕНО: сразу запускаем локальный кулдаун, чтобы не накликали ещё ===
      setCooldownLeft(CLIENT_COOLDOWN_SEC);
    } catch (e) {
      console.error(e);
      setError("Не удалось отправить сообщение.");
    } finally {
      setSending(false);
    }
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Служба поддержки</h1>
          <a
            href="/"
            className="inline-flex items-center rounded-lg border px-3 py-2 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50"
          >
            Закрыть
          </a>
        </div>
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Служба поддержки</h1>
          <a
            href="/"
            className="inline-flex items-center rounded-lg border px-3 py-2 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50"
          >
            Закрыть
          </a>
        </div>
        <div className="text-gray-500">Инициализация...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Служба поддержки</h1>
        <a
            href="/"
            className="inline-flex items-center rounded-lg border px-3 py-2 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50"
          >
            Закрыть
          </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Список тикетов */}
        <aside className="md:col-span-1 border rounded-xl overflow-hidden">
          <div className="p-3 border-b bg-gray-50 font-semibold">
            Ваши обращения
          </div>

          <div className="max-h-[60vh] overflow-auto">
            {ticketsLoading ? (
              <div className="p-3 text-gray-500">Загрузка...</div>
            ) : tickets.length === 0 ? (
              <div className="p-3 text-gray-500">У вас пока нет обращений.</div>
            ) : (
              <ul>
                {tickets.map((t) => (
                  <li
                    key={t.id}
                    className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                      t.id === selectedId ? "bg-amber-50" : ""
                    }`}
                    onClick={() => setSelectedId(t.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">
                        #{t.id} {t.subject || "Без темы"}
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          statusBadge[t.status] || "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {t.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t.category || "общий вопрос"}
                      {t.unread_for_user ? (
                        <span className="ml-2 inline-block text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                          новое
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Форма создания тикета */}
          <div className="p-3 border-t">
            <div className="text-sm font-semibold mb-2">Отправить обращение</div>
            <form onSubmit={handleCreateTicket} className="flex flex-col gap-2">
              <input
                className="border rounded-lg px-3 py-2 text-sm"
                placeholder="Тема"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
              />
              <select
                className="border rounded-lg px-3 py-2 text-sm"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              >
                <option value="">Категория (необязательно)</option>
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              <textarea
                className="border rounded-lg px-3 py-2 text-sm"
                placeholder="Опишите ваш вопрос"
                rows={3}
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
              />
            <button
  className="bg-[#16a34a] hover:bg-[#15803d] active:bg-[#166534] text-white rounded-lg py-2 text-sm font-semibold disabled:opacity-50"
  disabled={creating}
>
  {creating ? "Отправка..." : "Отправить"}
</button>
            </form>
          </div>
        </aside>

        {/* Чат по тикету */}
        {/* ДОБАВЛЕНО: фиксируем высоту секции, чтобы внутри появился скролл сообщений */}
        <section className="md:col-span-2 border rounded-xl flex flex-col overflow-hidden h-[70vh]">
          <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
            {selectedTicket ? (
              <>
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    #{selectedTicket.id} {selectedTicket.subject || "Без темы"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedTicket.category || "общий вопрос"}
                  </div>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    statusBadge[selectedTicket.status] ||
                    "bg-gray-200 text-gray-700"
                  }`}
                >
                  {selectedTicket.status}
                </span>
              </>
            ) : (
              <div className="text-gray-500 text-sm">
                Выберите обращение слева или создайте новое.
              </div>
            )}
          </div>

          {/* История сообщений */}
          {/* ДОБАВЛЕНО: скроллим только по вертикали */}
          <div className="flex-1 p-3 overflow-y-auto bg-white">
            {selectedTicket ? (
              messagesLoading ? (
                <div className="text-gray-500">Загрузка...</div>
              ) : messages.length === 0 ? (
                <div className="text-gray-500">Сообщений пока нет.</div>
              ) : (
                <ul className="space-y-2">
                  {messages.map((m) => (
                    <li
                      key={m.id}
                      className={`flex ${
                        m.author_type === "user"
                          ? "justify-end"
                          : m.author_type === "agent"
                          ? "justify-start"
                          : "justify-center"
                      }`}
                    >
                      <div
                        className={[
                          "max-w-[80%] rounded-xl px-3 py-2 text-sm",
                          m.author_type === "user"
                            ? "bg-amber-100"
                            : "bg-gray-100",
                        ].join(" ")}
                      >
                        <div className="whitespace-pre-wrap">{m.body}</div>
                        <div className="text-[10px] text-gray-500 mt-1">
                          {new Date(m.date_created || "").toLocaleString()}
                        </div>
                      </div>
                    </li>
                  ))}
                  <div ref={chatEndRef} />
                </ul>
              )
            ) : (
              <div className="text-gray-500 text-sm">
                Здесь появится переписка по выбранному обращению.
              </div>
            )}
          </div>

          {/* === ДОБАВЛЕНО: баннеры подсказок над композером === */}
          {cooldownActive && (
            <div className="mx-3 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Пожалуйста, подождите {cooldownLeft} сек перед следующей отправкой.
            </div>
          )}
          {streakBlocked && !cooldownActive && (
            <div className="mx-3 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Дождитесь ответа поддержки, затем отправьте следующее сообщение (лимит {MAX_USER_STREAK} подряд).
            </div>
          )}

          {/* Composer */}
          <div className="p-3 border-t bg-gray-50">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <textarea
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
                placeholder={
                  selectedTicket
                    ? cooldownActive
                      ? `Подождите ${cooldownLeft} сек...`
                      : streakBlocked
                        ? "Дождитесь ответа поддержки…"
                        : "Напишите сообщение..."
                    : "Выберите обращение слева, чтобы написать"
                }
                rows={2}
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                disabled={!selectedTicket || sending || cooldownActive || streakBlocked}
              />
             <button
  className="bg-[#16a34a] hover:bg-[#15803d] active:bg-[#166534] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
  disabled={!selectedTicket || sending || !newBody.trim() || cooldownActive || streakBlocked}
>
  {sending ? "Отправка..." : "Отправить"}
</button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SupportWidget;
